import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { errorMessage } from '@/lib/errors';
import { useAuthStore } from '@/stores/auth-store';
import { portalErrorCode, usePartnerMe } from '@/pages/partner-portal/portal-api';
import { PartnerPendingPage } from '@/pages/partner-portal/PartnerPendingPage';

/**
 * The gate in front of every partner portal page: a partner session, then a profile the portal
 * agrees to serve. A staff session is sent back to the console; a pending or blocked partner
 * sees why instead of the menu.
 */
export function PartnerRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isPartner = useAuthStore((s) => s.subjectKind === 'partner');
  const me = usePartnerMe(isAuthenticated && isPartner);

  if (!isAuthenticated) return <Navigate to="/partner/login" state={{ from: location }} replace />;
  if (!isPartner) return <Navigate to="/" replace />;

  if (me.isError) {
    const code = portalErrorCode(me.error);
    if (code === 'PART-423') return <PartnerPendingPage />;
    if (code === 'PART-424') return <PartnerPendingPage blocked />;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-4 text-center">
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(me.error, t('common.errorGeneric'))}
        </p>
        <Button variant="outline" onClick={() => void me.refetch()}>
          {t('partnerPortal.layout.retry')}
        </Button>
      </div>
    );
  }

  if (!me.data) {
    return (
      <div className="p-6 text-sm text-muted-foreground" role="status">
        {t('common.loading')}
      </div>
    );
  }

  return <>{children}</>;
}
